import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
	createSeatDecision,
	seatLayoutFor,
	venueById,
	type SeatCandidate,
} from "@anjabom/core";
import { contributionRequest } from "../lib/contribution-client";
import { parseSharedSeatKey } from "../lib/seat-share";
import { useLocalState } from "../state/LocalStateProvider";
interface Poll {
	id: string;
	showTitle?: string;
	candidates: Array<{
		seatKey: string;
		label: string;
		expectedPrice?: number | null;
	}>;
	expired: boolean;
	host: boolean;
	expiresAt: string;
	ownChoice: number | null;
	votes: Array<{ choice: number; count: number }>;
	comments: Array<{ comment: string; id?: string }>;
}
export function PollPage() {
	const { id } = useParams(),
		{ state, saveSeatDecision } = useLocalState();
	const [poll, setPoll] = useState<Poll | null>(null),
		[error, setError] = useState(""),
		[comment, setComment] = useState(""),
		[retry, setRetry] = useState(0),
		[saved, setSaved] = useState(false);
	useEffect(() => {
		let live = true;
		contributionRequest<Poll>(`/api/polls/${id}`)
			.then((p) => {
				if (live) {
					setPoll(p);
					setError("");
				}
			})
			.catch((e) => {
				if (live) setError(e.message);
			});
		return () => {
			live = false;
		};
	}, [id, retry]);
	async function vote(choice: number) {
		try {
			await contributionRequest(`/api/polls/${id}/vote`, "POST", {
				choice,
				comment,
			});
			setRetry(retry + 1);
		} catch (e) {
			setError(e instanceof Error ? e.message : "연결되면 다시 눌러 주세요.");
		}
	}
	function decide(choice: number) {
		if (!poll) return;
		const candidates: SeatCandidate[] = poll.candidates.flatMap((item, i) => {
			const seat = parseSharedSeatKey(item.seatKey),
				venue = seat ? venueById(seat.venueId) : null;
			const layout = seat
				? seatLayoutFor(
						seat.venueId,
						venue?.spaces.find(
							(s) =>
								seatLayoutFor(seat.venueId, s.id)?.layoutVersionId ===
								seat.layoutVersionId,
						)?.id ?? "",
					)
				: null;
			const space = venue?.spaces.find((s) => s.id === layout?.spaceId);
			return seat && venue && space
				? [
						{
							...seat,
							candidateId: `poll-${id}-${i}`,
							categoryId: venue.categoryId,
							venueName: venue.name,
							spaceId: space.id,
							spaceName: space.name,
							instanceId: null,
							instanceLabel: poll.showTitle ?? null,
							expectedPrice: item.expectedPrice ?? null,
						},
					]
				: [];
		});
		const chosen = candidates[choice];
		if (!chosen) {
			setError("좌석을 다시 확인해 주세요.");
			return;
		}
		const decision = createSeatDecision(
			candidates,
			state.viewingPlan,
			chosen.candidateId,
			[],
			[],
			"nearby_ok",
			"일행 투표 결과로 정했어요.",
		);
		if (saveSeatDecision(decision)) {
			setSaved(true);
			setError("");
		} else setError("이 기기에 결정을 저장하지 못했어요.");
	}
	return (
		<section className="page narrow stack-lg">
			<h1>{poll?.showTitle ? `${poll.showTitle} · ` : ""}좌석 골라 주세요</h1>
			{error ? (
				<p role="alert">
					{error}
					<button className="button" onClick={() => setRetry(retry + 1)}>
						다시 확인하기
					</button>
				</p>
			) : null}
			{!poll ? (
				<p role="status">투표를 불러오고 있어요.</p>
			) : (
				<>
					<p>
						{poll.expired
							? "투표가 끝났어요. 결과를 확인해요."
							: `한 기기에서 한 표, 바꿔 고를 수 있어요. ${poll.expiresAt.slice(0, 10)}까지예요.`}
					</p>
					{!poll.expired ? (
						<label className="field">
							한마디 (선택, 이름·연락처 없이 40자)
							<input
								className="input"
								maxLength={40}
								value={comment}
								onChange={(e) => setComment(e.target.value)}
							/>
						</label>
					) : null}
					{poll.candidates.map((candidate, i) => {
						const count = poll.votes.find((v) => v.choice === i)?.count ?? 0;
						return (
							<article className="tip-item stack" key={candidate.seatKey}>
								<Link to={`/seat/${encodeURIComponent(candidate.seatKey)}`}>
									{candidate.label}
								</Link>
								<p>
									{count}표{poll.ownChoice === i ? " · 내가 고른 자리" : ""}
								</p>
								{!poll.expired ? (
									<button
										className="button button-primary"
										onClick={() => void vote(i)}
									>
										이 자리 고르기
									</button>
								) : null}
								{poll.host ? (
									<button className="button" onClick={() => decide(i)}>
										이 자리로 결정하기
									</button>
								) : null}
							</article>
						);
					})}
					{poll.votes.length === 0 ? (
						<p>
							아직 아무도 고르지 않았어요. 링크를 일행에게 다시 보내 주세요.
						</p>
					) : null}
					{poll.comments.map((item, i) => (
						<blockquote key={item.id ?? i}>
							{item.comment}
							{poll.host && item.id ? (
								<button
									className="button"
									onClick={() =>
										void contributionRequest(
											`/api/polls/${id}/comments/${item.id}`,
											"PATCH",
											{},
										)
											.then(() => setRetry(retry + 1))
											.catch((e) => setError(e.message))
									}
								>
									이 한마디 숨기기
								</button>
							) : null}
						</blockquote>
					))}
					{saved ? (
						<p role="status">
							이 기기에 결정을 저장했어요.{" "}
							<Link to="/passport">기록에서 다시 보기</Link>
						</p>
					) : null}
				</>
			)}
		</section>
	);
}
